import { useEffect, useState } from 'react';

import { InboxOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Switch,
  Upload,
  theme,
} from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  CreateDashboardVideoInput,
  DashboardVideoDetail,
  ThumbnailUploadUrlResponse,
  VideoUploadUrlResponse,
} from 'models/DashboardVideo';

import { DashboardVideoService } from 'services/DashboardVideoService';

import appI18n from '../../../config/i18n';
import { appendDiscarded, buildUpdatePayload } from '../utils/videoPayload';
import FramePickerModal from './FramePickerModal';

const { Dragger } = Upload;

const COUNTRY_OPTIONS = [{ value: 'CL' }, { value: 'PE' }, { value: 'CO' }];
const DEFAULT_COUNTRY = 'CL';
const MAX_VIDEO_SIZE_MB = 250;

interface FormValues {
  countryCode: string;
  title: string;
  description: string;
  isMandatory: boolean;
}

interface Props {
  isOpen: boolean;
  mode?: 'create' | 'edit';
  video?: DashboardVideoDetail | null;
  loading?: boolean;
  defaultCountry?: string;
  handleModalCancel: () => void;
  onSaved: () => void;
}

/**
 * Lee la duración (en segundos, redondeada) directamente de los metadatos del
 * archivo en el navegador, antes de subirlo. El BFF NO la extrae del archivo;
 * el cliente la mide y la envía en el create/patch.
 */
const getVideoDurationSeconds = (file: File): Promise<number> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Math.round(video.duration));
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo leer la duración del video'));
    };
    video.src = url;
  });

function CreateVideoModal({
  isOpen,
  mode = 'create',
  video,
  loading = false,
  defaultCountry,
  handleModalCancel,
  onSaved,
}: Props) {
  const isEditMode = mode === 'edit';
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [form] = Form.useForm<FormValues>();

  const [submitting, setSubmitting] = useState<boolean>(false);

  // Estado del flujo de upload a R2.
  const [r2Key, setR2Key] = useState<string | null>(null);
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [uploadPercent, setUploadPercent] = useState<number>(0);
  // true cuando el usuario subió un .mp4 nuevo en esta sesión del modal.
  const [videoReplaced, setVideoReplaced] = useState<boolean>(false);

  // Estado del flujo de thumbnail.
  const [thumbnailR2Key, setThumbnailR2Key] = useState<string | null>(null);
  const [thumbnailSource, setThumbnailSource] = useState<
    'frame' | 'custom' | null
  >(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | null>(
    null
  );
  const [thumbnailUploading, setThumbnailUploading] = useState<boolean>(false);
  const [discardedKeys, setDiscardedKeys] = useState<string[]>([]);
  const [frameModalOpen, setFrameModalOpen] = useState<boolean>(false);

  // Apertura del modal: reset de estado y, en creación, valores por defecto.
  useEffect(() => {
    if (!isOpen) return;
    form.resetFields();
    setR2Key(null);
    setDurationSeconds(null);
    setUploading(false);
    setUploadedFileName(null);
    setUploadedFileUrl(null);
    setUploadPercent(0);
    setVideoReplaced(false);
    setThumbnailR2Key(null);
    setThumbnailSource(null);
    setThumbnailPreviewUrl(null);
    setThumbnailUploading(false);
    setDiscardedKeys([]);
    setFrameModalOpen(false);

    if (mode === 'create') {
      form.setFieldsValue({
        countryCode: defaultCountry || DEFAULT_COUNTRY,
        isMandatory: false,
      });
    }
  }, [isOpen]);

  // Edición: poblar el formulario cuando llega el detalle (puede tardar; se
  // muestra un skeleton mientras tanto).
  useEffect(() => {
    if (!isOpen || mode !== 'edit' || !video) return;
    form.setFieldsValue({
      countryCode: video.countryCode,
      title: video.title,
      description: video.description,
      isMandatory: video.isMandatory,
    });
    // El detalle expone r2Key y durationSeconds; videoUrl (presigned GET) se
    // usa para previsualizar el video existente cuando el BFF lo expone.
    setR2Key(video.r2Key);
    setDurationSeconds(video.durationSeconds);
    setVideoReplaced(false);
    setThumbnailR2Key(video.thumbnailR2Key);
    setThumbnailSource(video.thumbnailR2Key ? 'custom' : null);
    setThumbnailPreviewUrl(video.thumbnailUrl);
  }, [isOpen, video]);

  const countryOptions = COUNTRY_OPTIONS.map((opt) => {
    const code = opt.value.toLowerCase();
    return {
      value: opt.value,
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <img
            src={`https://flagcdn.com/16x12/${code}.png`}
            srcSet={`https://flagcdn.com/32x24/${code}.png 2x`}
            width={16}
            height={12}
            alt=""
            style={{ display: 'block' }}
          />
          <span>
            {t(`scenes.videos.scene.countries.${opt.value}`, opt.value)}
          </span>
        </span>
      ),
    };
  });

  /**
   * Flujo de upload de 3 pasos para el video .mp4:
   * 1. Leer la duración de los metadatos del archivo en el navegador.
   * 2. Pedir la presigned PUT URL al BFF (getVideoUploadUrl).
   * 3. PUT directo al bucket con axios LIMPIO (sin interceptores que rompan la
   *    firma de R2) y guardar r2Key + durationSeconds para el submit.
   * Devuelve false para evitar el upload automático de antd.
   */
  const beforeUpload = async (file: File): Promise<boolean> => {
    if (file.type !== 'video/mp4') {
      message.error(t('scenes.videos.scene.create.errors.video_type'), 3.5);
      return false;
    }

    if (file.size / 1024 / 1024 > MAX_VIDEO_SIZE_MB) {
      message.error(
        t('scenes.videos.scene.create.validations.video_too_large', {
          max: MAX_VIDEO_SIZE_MB,
        }),
        3.5
      );
      return false;
    }

    // Paso 1: duración desde los metadatos del navegador.
    let measuredDuration: number;
    try {
      measuredDuration = await getVideoDurationSeconds(file);
    } catch (e) {
      message.error(
        t('scenes.videos.scene.create.errors.duration_failed'),
        3.5
      );
      return false;
    }
    if (!measuredDuration || measuredDuration <= 0) {
      message.error(
        t('scenes.videos.scene.create.errors.duration_failed'),
        3.5
      );
      return false;
    }

    setUploading(true);
    setUploadPercent(0);
    setR2Key(null);
    setUploadedFileName(file.name);
    setUploadedFileUrl(null);

    // Paso 2: presigned URL.
    const up = await DashboardVideoService.getVideoUploadUrl({
      filename: file.name,
    });
    if (!up.success) {
      const error = up.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.videos.scene.create.errors.upload_url_failed'),
        3.5
      );
      setUploading(false);
      setUploadedFileName(null);
      return false;
    }

    const { uploadUrl, r2Key: newR2Key } = up.data as VideoUploadUrlResponse;

    // Paso 3: PUT directo a R2 con axios limpio (sin MainBackendAxios).
    try {
      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': 'video/mp4' },
        onUploadProgress: (e) => {
          if (e.total) {
            setUploadPercent(Math.round((e.loaded / e.total) * 100));
          }
        },
      });
    } catch (e) {
      message.error(t('scenes.videos.scene.create.errors.upload_failed'), 3.5);
      setUploading(false);
      setUploadedFileName(null);
      return false;
    }

    setDiscardedKeys((prev) => {
      let next = appendDiscarded(prev, videoReplaced ? r2Key : null);
      next = appendDiscarded(next, thumbnailR2Key);
      return next;
    });
    // el thumbnail viejo deja de tener sentido: reset
    if (thumbnailPreviewUrl && thumbnailSource === 'frame') {
      URL.revokeObjectURL(thumbnailPreviewUrl);
    }
    setThumbnailR2Key(null);
    setThumbnailSource(null);
    setThumbnailPreviewUrl(null);

    setR2Key(newR2Key);
    setDurationSeconds(measuredDuration);
    setVideoReplaced(true);
    // Blob URL local para previsualizar el video recién subido (abrir en otra
    // pestaña), igual que el video existente de la BD.
    setUploadedFileUrl(URL.createObjectURL(file));
    setUploadPercent(100);
    setUploading(false);
    message.success(t('scenes.videos.scene.create.video_uploaded'), 2.5);
    return false;
  };

  /** Sube un blob/File de thumbnail a R2 vía presigned y deja el r2Key en estado. */
  const uploadThumbnail = async (
    file: Blob,
    filename: string,
    source: 'frame' | 'custom'
  ) => {
    setThumbnailUploading(true);
    const up = await DashboardVideoService.getThumbnailUploadUrl({
      filename,
      contentType: file.type,
    });
    if (!up.success) {
      const error = up.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.videos.scene.create.errors.thumbnail_upload_url_failed'),
        3.5
      );
      setThumbnailUploading(false);
      return;
    }
    const {
      uploadUrl,
      r2Key: newKey,
      contentType,
    } = up.data as ThumbnailUploadUrlResponse;
    try {
      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': contentType },
      });
    } catch (e) {
      message.error(
        t('scenes.videos.scene.create.errors.thumbnail_upload_failed'),
        3.5
      );
      setThumbnailUploading(false);
      return;
    }
    // reemplaza el thumbnail anterior → su key vieja a descartes
    setDiscardedKeys((prev) => appendDiscarded(prev, thumbnailR2Key));
    if (thumbnailPreviewUrl && thumbnailSource === 'frame') {
      URL.revokeObjectURL(thumbnailPreviewUrl);
    }
    setThumbnailR2Key(newKey);
    setThumbnailSource(source);
    setThumbnailPreviewUrl(URL.createObjectURL(file));
    setThumbnailUploading(false);
  };

  const handleFramePicked = async (blob: Blob, atSeconds: number) => {
    setFrameModalOpen(false);
    await uploadThumbnail(blob, `frame-${Math.round(atSeconds)}.webp`, 'frame');
  };

  const THUMB_ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
  const THUMB_MAX_MB = 5;

  const handleCustomImage = async (file: File) => {
    if (!THUMB_ALLOWED.includes(file.type)) {
      message.error(t('scenes.videos.scene.create.errors.thumbnail_type'), 3.5);
      return false;
    }
    if (file.size / 1024 / 1024 > THUMB_MAX_MB) {
      message.error(
        t('scenes.videos.scene.create.errors.thumbnail_too_large', {
          max: THUMB_MAX_MB,
        }),
        3.5
      );
      return false;
    }
    await uploadThumbnail(file, file.name, 'custom');
    return false; // evita el upload automático de antd
  };

  const handleRemoveThumbnail = () => {
    setDiscardedKeys((prev) => appendDiscarded(prev, thumbnailR2Key));
    if (thumbnailPreviewUrl && thumbnailSource === 'frame') {
      URL.revokeObjectURL(thumbnailPreviewUrl);
    }
    setThumbnailR2Key(null);
    setThumbnailSource(null);
    setThumbnailPreviewUrl(null);
  };

  const handleClose = () => {
    if (uploadedFileUrl) URL.revokeObjectURL(uploadedFileUrl);
    if (thumbnailPreviewUrl && thumbnailPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(thumbnailPreviewUrl);
    }
    handleModalCancel();
  };

  const handleCreate = async (values: FormValues) => {
    if (!r2Key || !durationSeconds || durationSeconds <= 0) {
      message.error(
        t('scenes.videos.scene.create.validations.video_required'),
        3.5
      );
      return;
    }

    const body: CreateDashboardVideoInput = {
      countryCode: values.countryCode,
      title: values.title.trim(),
      description: values.description.trim(),
      r2Key,
      durationSeconds,
      isMandatory: values.isMandatory,
    };
    if (thumbnailR2Key) body.thumbnailR2Key = thumbnailR2Key;
    if (discardedKeys.length > 0) body.discardedR2Keys = discardedKeys;

    setSubmitting(true);
    const response = await DashboardVideoService.createDashboardVideo(body);
    setSubmitting(false);

    if (response.success) {
      handleModalCancel();
      onSaved();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.videos.scene.create.errors.failed'),
        3.5
      );
    }
  };

  const handleUpdate = async (values: FormValues) => {
    if (!video) return;
    const body = buildUpdatePayload(video, {
      title: values.title.trim(),
      description: values.description.trim(),
      isMandatory: values.isMandatory,
      videoReplaced,
      r2Key,
      durationSeconds,
      thumbnailR2Key,
      discardedR2Keys: discardedKeys,
    });

    setSubmitting(true);
    const response = await DashboardVideoService.updateDashboardVideo(
      video.id,
      body
    );
    setSubmitting(false);

    if (response.success) {
      handleModalCancel();
      onSaved();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.videos.scene.create.errors.update_failed'),
        3.5
      );
    }
  };

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }

    if (isEditMode && video) {
      await handleUpdate(values);
    } else {
      await handleCreate(values);
    }
  };

  const modalTitle = isEditMode
    ? t('scenes.videos.scene.create.edit_title')
    : t('scenes.videos.scene.create.title');
  const finalOkText = isEditMode
    ? t('scenes.videos.scene.create.buttons.save')
    : t('scenes.videos.scene.create.buttons.create');

  // Lista de archivos del Dragger:
  // - subiendo: item con barra de progreso (status uploading + percent).
  // - recién subido: item clickeable que abre el blob local en otra pestaña.
  // - edición con video existente: item con la presigned URL del detalle.
  const buildUploadFileList = (): UploadFile[] => {
    if (uploading) {
      return [
        {
          uid: 'uploading',
          name: uploadedFileName || 'video.mp4',
          status: 'uploading',
          percent: uploadPercent,
        },
      ];
    }
    if (videoReplaced && uploadedFileName && uploadedFileUrl) {
      return [
        {
          uid: '-1',
          name: uploadedFileName,
          status: 'done',
          url: uploadedFileUrl,
        },
      ];
    }
    if (isEditMode && video?.videoUrl && !videoReplaced) {
      return [
        {
          uid: 'current',
          name: t('scenes.videos.scene.create.current_video'),
          status: 'done',
          url: video.videoUrl,
        },
      ];
    }
    return [];
  };
  const uploadFileList: UploadFile[] = buildUploadFileList();

  const footer = (
    <Space>
      <Button onClick={handleClose}>
        {t('scenes.videos.scene.create.buttons.cancel')}
      </Button>
      <Button
        type="primary"
        loading={submitting}
        disabled={loading || (isEditMode && !video)}
        onClick={handleSubmit}
      >
        {finalOkText}
      </Button>
    </Space>
  );

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={handleClose}
      footer={footer}
      destroyOnClose
      width={720}
      maskClosable={false}
      style={{ top: 32 }}
      styles={{
        footer: {
          marginTop: 0,
          paddingTop: 16,
          borderTop: `1px solid ${token.colorSplit}`,
        },
      }}
    >
      <Divider style={{ marginTop: '12px', marginBottom: '20px' }} />

      <Form form={form} layout="vertical" preserve={false}>
        {loading ? (
          <div>
            <Skeleton.Input active block style={{ marginBottom: 16 }} />
            <Skeleton.Input active block style={{ marginBottom: 16 }} />
            <Skeleton active title={false} paragraph={{ rows: 3 }} />
          </div>
        ) : (
          <>
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name="countryCode"
                  label={t('scenes.videos.scene.create.labels.country')}
                  rules={[{ required: true }]}
                >
                  <Select options={countryOptions} disabled={isEditMode} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="title"
                  label={t('scenes.videos.scene.create.labels.title')}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'scenes.videos.scene.create.validations.title_required'
                      ),
                    },
                  ]}
                >
                  <Input
                    placeholder={t(
                      'scenes.videos.scene.create.placeholders.title'
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="description"
                  label={t('scenes.videos.scene.create.labels.description')}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'scenes.videos.scene.create.validations.description_required'
                      ),
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={3}
                    placeholder={t(
                      'scenes.videos.scene.create.placeholders.description'
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label={t('scenes.videos.scene.create.labels.video_file')}
              required
            >
              <Dragger
                accept="video/mp4"
                maxCount={1}
                multiple={false}
                beforeUpload={beforeUpload}
                fileList={uploadFileList}
                disabled={uploading}
                onRemove={() => {
                  if (uploadedFileUrl) URL.revokeObjectURL(uploadedFileUrl);
                  setR2Key(isEditMode && video ? video.r2Key : null);
                  setDurationSeconds(
                    isEditMode && video ? video.durationSeconds : null
                  );
                  setUploadedFileName(null);
                  setUploadedFileUrl(null);
                  setUploadPercent(0);
                  setVideoReplaced(false);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {t('scenes.videos.scene.create.upload_drag_text')}
                </p>
                <p className="ant-upload-hint">
                  {t('scenes.videos.scene.create.hints.video', {
                    max: MAX_VIDEO_SIZE_MB,
                  })}
                </p>
              </Dragger>
            </Form.Item>

            {r2Key && (
              <Form.Item
                label={t('scenes.videos.scene.create.thumbnail_label')}
                tooltip={t('scenes.videos.scene.create.thumbnail_hint')}
                style={{ marginTop: 8 }}
              >
                {thumbnailPreviewUrl ? (
                  <Space align="start" size={16}>
                    <div style={{ position: 'relative', width: 220 }}>
                      <img
                        src={thumbnailPreviewUrl}
                        alt=""
                        style={{
                          width: 220,
                          aspectRatio: '16/9',
                          objectFit: 'cover',
                          borderRadius: 6,
                          display: 'block',
                        }}
                      />
                      {thumbnailUploading && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(255, 255, 255, 0.65)',
                            borderRadius: 6,
                          }}
                        >
                          <Spin />
                        </div>
                      )}
                    </div>
                    <Space direction="vertical" size={8}>
                      <span
                        style={{
                          color: token.colorTextSecondary,
                          fontSize: 12,
                        }}
                      >
                        {thumbnailSource === 'custom'
                          ? t(
                              'scenes.videos.scene.create.thumbnail_badge_custom'
                            )
                          : t(
                              'scenes.videos.scene.create.thumbnail_badge_frame'
                            )}
                      </span>
                      <Button
                        size="small"
                        disabled={thumbnailUploading}
                        onClick={() => setFrameModalOpen(true)}
                      >
                        {t('scenes.videos.scene.create.thumbnail_change_frame')}
                      </Button>
                      <Upload
                        accept="image/jpeg,image/png,image/webp"
                        maxCount={1}
                        showUploadList={false}
                        disabled={thumbnailUploading}
                        beforeUpload={handleCustomImage}
                      >
                        <Button size="small" loading={thumbnailUploading}>
                          {t(
                            'scenes.videos.scene.create.thumbnail_change_upload'
                          )}
                        </Button>
                      </Upload>
                      <Button
                        size="small"
                        danger
                        disabled={thumbnailUploading}
                        onClick={handleRemoveThumbnail}
                      >
                        {t('scenes.videos.scene.create.thumbnail_remove')}
                      </Button>
                    </Space>
                  </Space>
                ) : (
                  <Space>
                    <Button
                      type="primary"
                      disabled={thumbnailUploading}
                      onClick={() => setFrameModalOpen(true)}
                    >
                      {t('scenes.videos.scene.create.thumbnail_generate')}
                    </Button>
                    <Upload
                      accept="image/jpeg,image/png,image/webp"
                      maxCount={1}
                      showUploadList={false}
                      disabled={thumbnailUploading}
                      beforeUpload={handleCustomImage}
                    >
                      <Button loading={thumbnailUploading}>
                        {t('scenes.videos.scene.create.thumbnail_upload')}
                      </Button>
                    </Upload>
                  </Space>
                )}
              </Form.Item>
            )}

            <FramePickerModal
              isOpen={frameModalOpen}
              videoUrl={uploadedFileUrl || video?.videoUrl || null}
              onCancel={() => setFrameModalOpen(false)}
              onPick={handleFramePicked}
            />

            <Form.Item
              name="isMandatory"
              label={t('scenes.videos.scene.create.labels.is_mandatory')}
              valuePropName="checked"
              tooltip={t('scenes.videos.scene.create.hints.is_mandatory')}
              style={{ marginTop: 24, marginBottom: 0 }}
            >
              <Switch />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
}

export default CreateVideoModal;
